import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useProjectStore } from '../store/projectStore';

/**
 * Current-Graph Location Indicator
 *
 * Shows the current graph's name behind a small pin button; clicking it
 * opens a floating card with the full ancestry as a top-to-bottom vertical
 * stack (root at top, current graph at bottom, connected by a line), each
 * entry a one-click jump target. Chosen after comparing it live against a
 * breadcrumb trail, a plain "Up + name" indicator, an indented jump
 * dropdown, and a dot stepper — all of which were tried and rejected first.
 *
 * **Interaction:**
 * - Click the pin button to open/close the path popover
 * - Click any entry in the popover to jump to that graph
 * - Click outside the popover, or press Esc, to close it
 * - Esc with the popover already closed goes to the parent graph directly
 *
 * **Data source:** the root-to-current ancestry path is computed
 * server-side (`GraphService::getBreadcrumbPath`, called from
 * `GraphController::show`) and captured into the store's `breadcrumb`
 * field by `openGraph`.
 *
 * @component
 * @returns {React.ReactElement | null} Location indicator or null if no project loaded
 */
export const GraphLocation: React.FC = () => {
    const currentProject = useProjectStore((state) => state.currentProject);
    const currentGraph = useProjectStore((state) => state.currentGraph);
    const breadcrumb = useProjectStore((state) => state.breadcrumb);
    const openGraph = useProjectStore((state) => state.openGraph);
    const [open, setOpen] = useState(false);
    const containerRef = useRef<HTMLDivElement>(null);

    const atRoot = breadcrumb.length < 2;

    /** Navigate to the parent graph — the second-to-last breadcrumb entry (the last is the current graph itself). No-ops at the root. */
    const goToParent = useCallback(() => {
        if (atRoot) return;
        const parent = breadcrumb[breadcrumb.length - 2];
        openGraph(parent.uuid);
    }, [atRoot, breadcrumb, openGraph]);

    useEffect(() => {
        const onEscape = (e: KeyboardEvent) => {
            if (e.key !== 'Escape') return;
            if (open) setOpen(false);
            else goToParent();
        };
        const onClickOutside = (e: MouseEvent) => {
            if (containerRef.current && !containerRef.current.contains(e.target as Node)) setOpen(false);
        };
        window.addEventListener('keydown', onEscape);
        document.addEventListener('mousedown', onClickOutside);
        return () => {
            window.removeEventListener('keydown', onEscape);
            document.removeEventListener('mousedown', onClickOutside);
        };
    }, [open, goToParent]);

    if (!currentProject) return null;

    return (
        <div className="position-relative" ref={containerRef}>
            <button
                type="button"
                className="btn btn-sm btn-outline-secondary d-flex align-items-center gap-1 text-truncate"
                onClick={() => setOpen((o) => !o)}
                title="Show location in the story"
            >
                <i className="ti ti-map-pin" />
                <span className="text-truncate">{currentGraph?.name || 'Root'}</span>
            </button>

            {open && (
                <div
                    className="card shadow-sm position-absolute"
                    style={{ top: '100%', left: 0, zIndex: 1050, minWidth: '220px', marginTop: '4px' }}
                >
                    <div className="card-body p-2">
                        {breadcrumb.map((crumb, idx) => (
                            <div key={crumb.uuid} className="d-flex align-items-stretch">
                                <div className="d-flex flex-column align-items-center" style={{ width: '16px' }}>
                                    <div
                                        className={`rounded-circle ${crumb.uuid === currentGraph?.uuid ? 'bg-primary' : 'bg-secondary'}`}
                                        style={{ width: '8px', height: '8px', flexShrink: 0, marginTop: '6px' }}
                                    />
                                    {idx < breadcrumb.length - 1 && (
                                        <div className="bg-secondary-lt flex-fill" style={{ width: '2px', minHeight: '14px' }} />
                                    )}
                                </div>
                                <a
                                    href="#"
                                    className={`px-2 py-1 text-truncate ${crumb.uuid === currentGraph?.uuid ? 'fw-bold text-body' : 'text-secondary'}`}
                                    onClick={(e) => {
                                        e.preventDefault();
                                        setOpen(false);
                                        if (crumb.uuid !== currentGraph?.uuid) openGraph(crumb.uuid);
                                    }}
                                    title={crumb.name}
                                >
                                    {crumb.name}
                                </a>
                            </div>
                        ))}
                    </div>
                </div>
            )}
        </div>
    );
};
